import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { Plus, Copy, Trash2, Search, Camera, Play, Calendar, Pencil, Check, X, Globe, Monitor, Images, Settings, Smartphone } from 'lucide-react'
import { useEventStore } from '@/store'
import { useI18n } from '@/lib/i18n'
import { slugify, uploadFile } from '@/lib/utils'
import { createDefaultEvent } from '@/lib/templates'
import { importEventZip } from '@/lib/zip'
import { reidentifyImportedConfig } from '@/lib/eventImport'
import type { EventConfig } from '@/types'
import { motion, AnimatePresence } from 'framer-motion'

const MODE_TAG = 'bg-background/80 text-foreground border border-border'

function InlineRename({ name, onSave }: { name: string; onSave: (n: string) => void }) {
  const [editing, setEditing] = useState(false)
  const [val, setVal] = useState(name)
  const inputRef = useRef<HTMLInputElement>(null)

  const commit = () => {
    const trimmed = val.trim()
    if (trimmed && trimmed !== name) onSave(trimmed)
    setEditing(false)
  }

  const cancel = () => {
    setVal(name)
    setEditing(false)
  }

  useEffect(() => {
    if (editing) inputRef.current?.focus()
  }, [editing])

  if (editing) {
    return (
      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        <input
          ref={inputRef}
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') cancel() }}
          className="rounded border border-primary bg-background px-2 py-0.5 text-sm font-semibold text-foreground focus:outline-none"
        />
        <button onClick={commit} className="rounded p-0.5 hover:bg-green-500/20 text-green-400"><Check className="h-3.5 w-3.5" /></button>
        <button onClick={cancel} className="rounded p-0.5 hover:bg-destructive/20 text-muted-foreground"><X className="h-3.5 w-3.5" /></button>
      </div>
    )
  }

  return (
    <div 
      className="flex items-center gap-1.5 group/rename cursor-pointer"
      onClick={(e) => { e.stopPropagation(); setEditing(true) }}
    >
      <span className="text-base font-semibold truncate hover:text-primary transition-colors">{name}</span>
      <button
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); setEditing(true) }}
        className="opacity-0 group-hover/rename:opacity-100 rounded p-0.5 hover:bg-muted text-muted-foreground transition-opacity"
        title="Rename"
      >
        <Pencil className="h-3 w-3" />
      </button>
    </div>
  )
}

function EventCard({ event, onDuplicate, onDelete, onRename }: { event: EventConfig; onDuplicate: () => void; onDelete: () => void; onRename: (n: string) => void }) {
  const setActiveEventId = useEventStore((s) => s.setActiveEventId)

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.96 }}
      className="group relative flex flex-col rounded-lg border border-border bg-card transition-colors hover:border-primary/40"
      onClick={() => setActiveEventId(event.id)}
    >
      {/* Top Banner with Secondary Actions */}
      <div className="relative h-24 overflow-hidden rounded-t-lg bg-secondary">
        {event.coverImage && (
          <img src={event.coverImage} className="absolute inset-0 h-full w-full object-cover" alt="" />
        )}
        <div className="absolute top-3 right-3 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={(e) => { e.stopPropagation(); onDuplicate() }}
            className="rounded-lg bg-background/80 p-2 text-muted-foreground hover:text-foreground transition-all border border-border/50"
            title="Duplicate"
          >
            <Copy className="h-4 w-4" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete() }}
            className="rounded-lg bg-destructive/10 p-2 text-destructive/70 hover:text-destructive hover:bg-destructive/20 transition-all border border-destructive/20"
            title="Delete"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
        
        {/* Badge for Mode */}
        <div className="absolute bottom-3 left-4 flex flex-wrap gap-1">
          {event.capture.enabledModes.slice(0, 3).map((m) => (
            <span key={m} className={`flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${MODE_TAG}`}>
              {m}
            </span>
          ))}
          {event.capture.enabledModes.length > 3 && (
            <span className="flex items-center gap-1 rounded-md bg-muted/80 px-2 py-0.5 text-[10px] font-bold text-muted-foreground">
              +{event.capture.enabledModes.length - 3}
            </span>
          )}
        </div>
      </div>

      <div className="p-5 flex flex-col gap-4">
        {/* Name + Date */}
        <div className="space-y-1">
          <InlineRename name={event.name} onSave={onRename} />
          <div className="flex items-center gap-3 text-[11px] font-medium text-muted-foreground/60">
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {new Date(event.updatedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
            </span>
            <span className="h-1 w-1 rounded-full bg-border" />
            <span className="flex items-center gap-1 uppercase tracking-tight font-bold text-primary/40">
              ID: {event.id}
            </span>
          </div>
        </div>

        {/* Action Grid */}
        <div className="grid grid-cols-2 gap-2">
          <Link
            to={`/admin/events/${event.id}`}
            onClick={(e) => e.stopPropagation()}
            className="flex items-center justify-center gap-2 rounded-xl bg-secondary/80 py-2.5 text-xs font-semibold text-secondary-foreground hover:bg-secondary transition-all border border-border/50"
          >
            <Settings className="h-3.5 w-3.5 opacity-60" /> Edit Event
          </Link>
          <a
            href={`/booth/${event.id}`}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="flex items-center justify-center gap-2 rounded-xl bg-primary py-2.5 text-xs font-bold text-primary-foreground hover:bg-primary/90 transition-all shadow-lg"
          >
            <Play className="h-3.5 w-3.5" /> Launch
          </a>
          <Link
            to={`/gallery?eventId=${event.id}`}
            onClick={(e) => e.stopPropagation()}
            className="flex items-center justify-center gap-2 rounded-xl bg-muted/40 py-2.5 text-[11px] font-medium text-muted-foreground hover:bg-muted/60 transition-all border border-border/30"
          >
            <Images className="h-3.5 w-3.5 opacity-50" /> Gallery
          </Link>
          <a
            href={`/kiosk/${event.id}`}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="flex items-center justify-center gap-2 rounded-xl bg-muted/40 py-2.5 text-[11px] font-medium text-muted-foreground hover:bg-muted/60 transition-all border border-border/30"
          >
            <Monitor className="h-3.5 w-3.5 opacity-50" /> Kiosk
          </a>
          <Link
            to={`/admin/remote/${event.id}`}
            onClick={(e) => e.stopPropagation()}
            className="col-span-2 flex items-center justify-center gap-2 rounded-xl bg-secondary py-2.5 text-[11px] font-bold text-muted-foreground hover:bg-secondary transition-all border border-border"
          >
            <Smartphone className="h-3.5 w-3.5" /> Open Remote Control
          </Link>
        </div>
      </div>
    </motion.div>
  )
}

export default function EventsPage() {
  const { t } = useI18n()
  const events = useEventStore((s) => s.events)
  const loadEvents = useEventStore((s) => s.loadEvents)
  const saveEvent = useEventStore((s) => s.saveEvent)
  const deleteEvent = useEventStore((s) => s.deleteEvent)
  const setActiveEventId = useEventStore((s) => s.setActiveEventId)
  const [search, setSearch] = useState('')
  const [importing, setImporting] = useState(false)
  const [showCreate, setShowCreate] = useState(false)
  const [conflictData, setConflictData] = useState<{
    config: EventConfig
    assets: Record<string, Blob>
    poses: Record<string, Blob>
  } | null>(null)

  useEffect(() => { loadEvents() }, [loadEvents])

  const filtered = events.filter((e) => e.name.toLowerCase().includes(search.toLowerCase()))

  const handleCreateConfirm = async (name: string) => {
    const slug = slugify(name)
    if (!slug) return
    const event = createDefaultEvent()
    event.id = slug
    event.name = name
    await saveEvent(event)
    setActiveEventId(event.id)
    setShowCreate(false)
  }

  const handleDuplicate = async (id: string) => {
    const res = await fetch(`/api/events/${id}/duplicate`, { method: 'POST' })
    if (res.ok) {
      const data = await res.json()
      useEventStore.getState().addEvent(data.config)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm(t('events.confirmDelete'))) return
    await fetch(`/api/events/${id}`, { method: 'DELETE' })
    deleteEvent(id)
  }

  const handleRename = async (event: EventConfig, name: string) => {
    const updated = { ...event, name, updatedAt: new Date().toISOString() }
    await saveEvent(updated)
  }

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setImporting(true)
    try {
      const result = await importEventZip(file)
      if (result) {
        const conflict = events.find((ev) => ev.id === result.config.id)
        if (conflict) {
          // Let the admin choose between replacing that event and creating a
          // second one, both paths run through reidentifyImportedConfig.
          setConflictData({ config: result.config, assets: result.assets, poses: result.poses })
        } else {
          const config = reidentifyImportedConfig(result.config, {
            id: result.config.id,
            name: result.config.name,
          })
          const updatedConfig = await uploadImportedAssets(config, result.assets, result.poses)
          await saveEvent(updatedConfig)
        }
      } else {
        alert('That file is not a PoseBox event export.')
      }
    } catch (err) {
      console.error('Import failed', err)
      alert('Import failed. See the console for details.')
    } finally {
      setImporting(false)
      e.target.value = ''
    }
  }

  const handleConflictConfirm = async (choice: 'replace' | 'new', newName?: string) => {
    if (!conflictData) return

    const target = events.find((ev) => ev.id === conflictData.config.id)
    const name = choice === 'new' && newName ? newName : conflictData.config.name
    const id = choice === 'new' && newName ? slugify(newName) : conflictData.config.id

    let updatedConfig = reidentifyImportedConfig(conflictData.config, {
      id,
      name,
      // Replacing keeps the live event's id, created date and share tokens, so
      // QR codes already printed for it keep resolving.
      keepTokensFrom: choice === 'replace' ? target : undefined,
    })

    updatedConfig = await uploadImportedAssets(updatedConfig, conflictData.assets, conflictData.poses)
    await saveEvent(updatedConfig)
    setConflictData(null)
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('events.title')}</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">{events.length} {t(events.length === 1 ? 'events.configured_one' : 'events.configured', { count: events.length })}</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="cursor-pointer rounded-lg border border-border bg-secondary px-4 py-2 text-sm font-medium hover:bg-secondary/80 transition-colors">
            {importing ? t('common.loading') : t('editor.importEvent')}
            <input type="file" accept=".zip" className="hidden" onChange={handleImport} />
          </label>
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            <Plus className="h-4 w-4" />
            {t('events.create')}
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-5">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('events.search')}
          className="w-full rounded-lg border border-input bg-card py-2.5 pl-9 pr-4 text-sm focus:border-primary/50 transition-colors"
        />
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border py-20 text-muted-foreground">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-xl bg-muted">
            <Camera className="h-7 w-7 opacity-60" />
          </div>
          <p className="text-base font-medium">{t('events.noEvents')}</p>
          <p className="mt-1 text-sm opacity-60">{t('events.noEventsDesc')}</p>
          <button
            onClick={() => setShowCreate(true)}
            className="mt-5 flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            <Plus className="h-4 w-4" /> {t('events.create')}
          </button>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          <AnimatePresence>
            {filtered.map((event) => (
              <EventCard
                key={event.id}
                event={event}
                onDuplicate={() => handleDuplicate(event.id)}
                onDelete={() => handleDelete(event.id)}
                onRename={(name) => handleRename(event, name)}
              />
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* Create Event Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setShowCreate(false)}>
          <CreateEventModal onConfirm={handleCreateConfirm} onCancel={() => setShowCreate(false)} t={t} />
        </div>
      )}

      {/* Import Conflict Modal */}
      {conflictData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <ImportConflictModal
            conflictData={conflictData}
            events={events}
            onConfirm={handleConflictConfirm}
            onCancel={() => setConflictData(null)}
            t={t}
          />
        </div>
      )}
    </div>
  )
}

function CreateEventModal({ onConfirm, onCancel, t }: { onConfirm: (name: string) => void; onCancel: () => void; t: any }) {
  const [name, setName] = useState('')
  const slug = slugify(name)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => { inputRef.current?.focus() }, [])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (slug) onConfirm(name.trim())
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="w-full max-w-md rounded-xl border border-border bg-card p-6 shadow-md"
      onClick={(e) => e.stopPropagation()}
    >
      <h2 className="text-xl font-bold mb-1">{t('events.createModal.title')}</h2>
      <p className="text-sm text-muted-foreground mb-5">{t('events.createModal.description')}</p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Event Name</label>
          <input
            ref={inputRef}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Verjaardag Kelly & Tobie"
            className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm focus:border-primary/50 transition-colors"
          />
        </div>

        <div className="space-y-1.5">
          <label className="text-sm font-medium flex items-center gap-1.5"><Globe className="h-3.5 w-3.5 text-muted-foreground" /> Event URL</label>
          <div className="flex items-center rounded-lg border border-input bg-muted/50 px-3 py-2.5 text-sm">
            <span className="text-muted-foreground">/booth/</span>
            <span className={`font-mono ${slug ? 'text-primary font-medium' : 'text-muted-foreground/40'}`}>
              {slug || 'event-name-here'}
            </span>
          </div>
          {name.trim() && !slug && (
            <p className="text-xs text-destructive">Name must contain at least one alphanumeric character</p>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onCancel} className="rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-muted transition-colors">{t('common.cancel')}</button>
          <button type="submit" disabled={!slug} className="rounded-lg bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-40 transition-colors">
            <Plus className="h-4 w-4 inline mr-1" /> {t('events.create')}
          </button>
        </div>
      </form>
    </motion.div>
  )
}

async function uploadImportedAssets(
  config: EventConfig,
  assets: Record<string, Blob>,
  poses: Record<string, Blob> = {}
): Promise<EventConfig> {
  const newAssetUrls: Record<string, string> = {}

  for (const [zipPath, blob] of Object.entries(assets)) {
    try {
      const filename = zipPath.split('/').pop() || 'file.png'
      const file = new File([blob], filename, { type: blob.type })
      const uploadedUrl = await uploadFile(file)
      if (uploadedUrl) {
        newAssetUrls[zipPath] = uploadedUrl
      }
    } catch (err) {
      console.error(`Failed to upload asset ${zipPath}:`, err)
    }
  }

  // Poses are restored under their original filename, because the config
  // references them by filename rather than by URL. The endpoint keeps the
  // uploaded name, so no path rewriting is needed.
  for (const [filename, blob] of Object.entries(poses)) {
    try {
      const form = new FormData()
      form.append('file', new File([blob], filename, { type: blob.type }))
      const res = await fetch('/api/poses/upload', { method: 'POST', body: form })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
    } catch (err) {
      console.error(`Failed to upload pose ${filename}:`, err)
    }
  }

  const replacePaths = (obj: any): any => {
    if (!obj) return obj
    if (typeof obj === 'string') {
      if (newAssetUrls[obj]) {
        return newAssetUrls[obj]
      }
      return obj
    }
    if (Array.isArray(obj)) {
      return obj.map(replacePaths)
    }
    if (typeof obj === 'object') {
      const out: any = {}
      for (const [k, v] of Object.entries(obj)) {
        out[k] = replacePaths(v)
      }
      return out
    }
    return obj
  }

  return replacePaths(config)
}

function ImportConflictModal({
  conflictData,
  events,
  onConfirm,
  onCancel,
  t,
}: {
  conflictData: { config: EventConfig; assets: Record<string, Blob>; poses: Record<string, Blob> }
  events: EventConfig[]
  onConfirm: (choice: 'replace' | 'new', newName?: string) => Promise<void>
  onCancel: () => void
  t: any
}) {
  const [choice, setChoice] = useState<'new' | 'replace'>('new')
  const [newName, setNewName] = useState(() => {
    const originalName = conflictData.config.name
    let candidate = `${originalName} (Imported)`
    let suffix = 1
    while (events.some((e) => e.name.toLowerCase() === candidate.toLowerCase() || e.id === slugify(candidate))) {
      candidate = `${originalName} (Imported ${suffix})`
      suffix++
    }
    return candidate
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  const newSlug = slugify(newName)
  const isSlugConflicting = events.some((e) => e.id === newSlug)
  const isNameEmpty = !newName.trim()
  const isConfirmDisabled = choice === 'new' && (isSlugConflicting || isNameEmpty || isSubmitting)

  const handleConfirm = async () => {
    setIsSubmitting(true)
    try {
      await onConfirm(choice, choice === 'new' ? newName.trim() : undefined)
    } catch (err) {
      console.error(err)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="w-full max-w-md rounded-xl border border-border bg-card p-6 shadow-md space-y-5 text-card-foreground"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex justify-between items-start gap-4">
        <div>
          <h2 className="text-xl font-bold mb-1">Import Conflict</h2>
          <p className="text-sm text-muted-foreground">
            An event with the ID <code className="font-mono text-primary bg-primary/10 px-1.5 py-0.5 rounded">{conflictData.config.id}</code> already exists in your workspace.
          </p>
        </div>
        <button
          onClick={onCancel}
          disabled={isSubmitting}
          className="rounded-lg p-1 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-50"
          aria-label="Close dialog"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="space-y-3">
        <label
          className={`flex flex-col gap-1.5 p-4 rounded-xl border cursor-pointer transition-all ${
            choice === 'new'
              ? 'border-primary bg-primary/5 shadow-md'
              : 'border-border bg-muted/20 hover:bg-muted/40'
          }`}
        >
          <div className="flex items-center gap-2">
            <input
              type="radio"
              checked={choice === 'new'}
              onChange={() => setChoice('new')}
              className="text-primary focus:ring-primary"
            />
            <span className="text-sm font-semibold">Create as new event (Recommended)</span>
          </div>
          {choice === 'new' && (
            <div className="mt-2.5 space-y-3 pl-5" onClick={(e) => e.stopPropagation()}>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">New Event Name</label>
                <input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:border-primary/50 focus:outline-none transition-colors"
                  placeholder="Event name"
                />
              </div>
              <div className="space-y-1">
                <span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                  <Globe className="h-3 w-3" /> Event URL
                </span>
                <div className="flex items-center rounded-lg border border-input bg-muted/50 px-3 py-2 text-xs">
                  <span className="text-muted-foreground/60">/booth/</span>
                  <span className={`font-mono ${newSlug ? 'text-primary font-medium' : 'text-muted-foreground/40'}`}>
                    {newSlug || 'event-name-here'}
                  </span>
                </div>
                {isNameEmpty && (
                  <p className="text-[11px] text-destructive font-medium">Event name cannot be empty</p>
                )}
                {newName.trim() && !newSlug && (
                  <p className="text-[11px] text-destructive font-medium">Name must contain at least one alphanumeric character</p>
                )}
                {newSlug && isSlugConflicting && (
                  <p className="text-[11px] text-destructive font-medium">An event with this URL already exists</p>
                )}
              </div>
            </div>
          )}
        </label>

        <label
          className={`flex flex-col gap-1.5 p-4 rounded-xl border cursor-pointer transition-all ${
            choice === 'replace'
              ? 'border-destructive bg-destructive/5 shadow-md shadow-destructive/5'
              : 'border-border bg-muted/20 hover:bg-muted/40'
          }`}
        >
          <div className="flex items-center gap-2">
            <input
              type="radio"
              checked={choice === 'replace'}
              onChange={() => setChoice('replace')}
              className="text-destructive focus:ring-destructive"
            />
            <span className="text-sm font-semibold">Overwrite existing event</span>
          </div>
          <p className="text-xs text-muted-foreground pl-5 leading-normal">
            This will completely replace all configurations and settings for the existing event <code className="font-mono">{conflictData.config.id}</code>. This action is irreversible.
          </p>
        </label>
      </div>

      <div className="flex justify-end gap-2 pt-2 border-t border-border">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-muted transition-colors disabled:opacity-50"
        >
          {t('common.cancel')}
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={isConfirmDisabled}
          className={`rounded-lg px-5 py-2 text-sm font-semibold text-white transition-colors flex items-center gap-1.5 ${
            choice === 'replace'
              ? 'bg-destructive hover:bg-destructive/90 disabled:bg-destructive/50'
              : 'bg-primary hover:bg-primary/90 disabled:bg-primary/50'
          }`}
        >
          {isSubmitting ? (
            <>
              <div className="h-3.5 w-3.5 animate-spin rounded-full border border-current border-t-transparent" />
              Importing...
            </>
          ) : (
            'Confirm Import'
          )}
        </button>
      </div>
    </motion.div>
  )
}

